import {
    ApplicationRef,
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    ElementRef,
    EnvironmentInjector,
    computed,
    effect,
    inject,
    input,
    output,
    untracked,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateService } from '@ngx-translate/core';
import type Board from '@highcharts/dashboards/es-modules/Dashboards/Board';
import { Dashboards } from './dashboards-core';
import { DashboardRegistryService } from './dashboard-registry.service';
import { bindWidgetHostContext } from './widgets/dashboard-widget-host';
import { withAuthoringConnectors } from './widgets/dashboard-data-binding';
import type { DashboardWidgetPool } from './widgets/dashboard-widget-registry';
import { activateEditMode, buildEditModeOptions } from './dashboard-edit-mode';
import type { DashboardDataProvider } from './data/dashboard-provider.types';
import type { DashboardEditMode, DashboardLayout } from './dashboard.types';

@Component({
    selector: 'pulse-dashboard',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'highcharts-light' },
    template: `<div #host class="pulse-dashboard__host"></div>`,
    styleUrl: './dashboard.component.scss',
})
export class DashboardComponent {
    private readonly registry = inject(DashboardRegistryService);
    private readonly envInjector = inject(EnvironmentInjector);
    private readonly appRef = inject(ApplicationRef);
    private readonly destroyRef = inject(DestroyRef);
    private readonly translate = inject(TranslateService);
    private readonly host = viewChild.required<ElementRef<HTMLDivElement>>('host');

    private readonly langChange = toSignal(this.translate.onLangChange);

    readonly layout = input.required<DashboardLayout>();
    readonly dataProviders = input<readonly DashboardDataProvider[]>([]);
    readonly editMode = input<DashboardEditMode>('none');
    readonly angularWidgets = input<DashboardWidgetPool>({});
    readonly boardReady = output<Board>();

    private board?: Board;
    private mount?: HTMLDivElement;

    private readonly buildInputs = computed(() => ({
        hostEl: this.host().nativeElement,
        layout: this.layout(),
        providers: this.dataProviders(),
        mode: this.editMode(),
        widgets: this.angularWidgets(),
        lang: this.editMode() === 'none' ? undefined : this.langChange(),
    }));

    constructor() {
        effect(() => {
            this.buildInputs();
            untracked(() => this.build());
        });

        this.destroyRef.onDestroy(() => {
            this.teardown();
            this.registry.releaseProviders(this);
        });
    }

    /** Forces a rebuild from the current inputs. */
    reload(): void {
        this.build();
    }

    private build(): void {
        const { hostEl, layout, providers, mode, widgets } = this.buildInputs();

        this.registry.registerDashboardWidgets(widgets);

        if (mode === 'none') {
            this.registry.ensureRegistered();
        } else {
            this.registry.ensureAuthoringRegistered(this.translate);
        }

        this.registry.registerProviders(this, providers);

        this.teardown();

        this.mount = document.createElement('div');
        this.mount.className = 'pulse-dashboard__board';
        hostEl.appendChild(this.mount);

        bindWidgetHostContext(this.mount, this.envInjector, this.appRef);

        this.board = Dashboards.board(this.mount, {
            ...(mode === 'none' ? layout : withAuthoringConnectors(layout)),
            editMode: buildEditModeOptions(mode, this.translate),
        });

        if (mode !== 'none') {
            activateEditMode(this.board);
        }

        this.boardReady.emit(this.board);
    }

    private teardown(): void {
        this.board?.destroy();
        this.board = undefined;
        this.mount?.remove();
        this.mount = undefined;
    }
}
